/**
 * Audit log, solo lectura.
 *
 * Se lee directo de Firestore con el SDK de cliente (CLAUDE.md "Colisiones resueltas"), no por un
 * endpoint. Esta pantalla es la que explica una confirmación a posteriori, así que pinta LOS
 * CRITERIOS aplicados y las cifras que se le mostraron a Ops, no solo el resultado: sin el criterio,
 * sabes qué se pagó pero no por qué se seleccionó (invariante 4).
 *
 * No refresca solo. Igual que el preview es una mutación explícita, aquí el botón es explícito: una
 * pantalla de evidencia que se recarga sola bajo el cursor de alguien que está leyendo es peor que
 * una que espera.
 */
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  ActionIcon,
  Alert,
  Badge,
  Button,
  Card,
  Collapse,
  Group,
  Loader,
  Modal,
  Stack,
  Text,
  Title,
  Tooltip,
  UnstyledButton,
} from '@mantine/core';
import {
  IconAlertTriangle,
  IconBan,
  IconChevronDown,
  IconChevronRight,
  IconCircleCheck,
  IconCircleX,
  IconClock,
  IconProgress,
  IconProgressAlert,
  IconX,
} from '@tabler/icons-react';

import {
  aplicarLatido,
  describirCriterios,
  puedeCancelar,
  type Confirmacion,
  type EstadoConfirmacion,
  type LatidoCola,
} from '../auditLog';
import { llamarCancelar } from '../firebase';
import { nombreDePrograma } from '../programas';
import { leerConfirmaciones } from '../auditLogFirestore';
import {
  nViajesQuePidenAtencion,
  nViajesRechazadosPorStripe,
  type EstadoConfirmacion as EstadoTransferenciasDeConfirmacion,
} from '../confirmStatus';
import { leerEstadosDeConfirmacion } from '../confirmStatusFirestore';
import { leerLatidoCola } from '../estadoColaFirestore';
import { calcularRestante, describirRestanteCorto, formatearDuracion, type Restante } from '../eta';
import { formatearEur, formatearNumero } from '../formato';
import { EstadoTransferencias } from './EstadoTransferencias';

interface Presentacion {
  etiqueta: string;
  color: string;
  Icono: typeof IconCircleCheck;
}

/**
 * Un color por estado, y SIEMPRE con icono y palabra: el color solo no distingue para quien no lo ve
 * igual, y aquí la diferencia entre "completado" y "sin resolver" es si alguien tiene que ir a
 * reconciliar un pago a medias.
 *
 * ICONOS DIBUJADOS, no glifos unicode. Antes eran `✓ ◐ ✕ ● ⚠`, y aparte de no ser un sistema de
 * iconos, `◐` no existe en todas las fuentes: en la máquina que no la tenga, el estado "parcial"
 * —el que avisa de un pago a medias— se veía como un cuadrado vacío. Los colores salen de las
 * rampas de marca vía el tema (ver theme.ts).
 */
const PRESENTACION: Record<EstadoConfirmacion, Presentacion> = {
  completado: { etiqueta: 'Completado', color: 'green', Icono: IconCircleCheck },
  parcial: { etiqueta: 'Parcial', color: 'orange', Icono: IconProgressAlert },
  fallido: { etiqueta: 'Fallido', color: 'red', Icono: IconCircleX },
  'en-curso': { etiqueta: 'En curso', color: 'rosaBB', Icono: IconProgress },
  // Gris y con reloj a propósito: no es un problema, es trabajo esperando su turno. Desde BB-1667 una
  // confirmación puede estar en cola horas si la cuota diaria está agotada, y eso es funcionamiento
  // normal — no algo que mande a nadie a reconciliar.
  'en-cola': { etiqueta: 'En cola', color: 'gray', Icono: IconClock },
  // Gris, no rojo: cancelar es una decisión tomada a propósito, no un fallo. Rojo la pondría al lado
  // de "Fallido" y haría que una lista con cancelaciones pareciera una lista de problemas.
  cancelada: { etiqueta: 'Cancelada', color: 'gray', Icono: IconBan },
  // BB-2625. Gris: la cola está pagando otra confirmación, y esperar es lo normal.
  'esperando-turno': { etiqueta: 'Esperando turno', color: 'gray', Icono: IconClock },
  // BB-2625. La única alarma de cola: ninguna tanda de nadie termina.
  'cola-parada': { etiqueta: 'La cola no avanza', color: 'red', Icono: IconAlertTriangle },
  'sin-resolver': { etiqueta: 'Sin resolver', color: 'red', Icono: IconAlertTriangle },
};

const CUANDO = new Intl.DateTimeFormat('es-ES', {
  day: '2-digit',
  month: '2-digit',
  year: 'numeric',
  hour: '2-digit',
  minute: '2-digit',
});

/** BB-1652: cada cuánto se relee el registro mientras hay una confirmación en curso. */
const MS_ENTRE_POLLINGS = 10_000;

/**
 * A partir de aquí, "en cola" se pinta en naranja y dice cuánto lleva.
 *
 * Cuatro horas no es un número mágico: es bastante más que lo que tarda una tanda normal en arrancar
 * (segundos) y bastante menos que una ventana de cuota (24 h), así que separa "esperando cuota, todo
 * bien" de "esto no ha arrancado y nadie se ha dado cuenta".
 */
const HORAS_EN_COLA_PARA_AVISAR = 4;

function formatearCuando(ms: number | null): string {
  return ms === null ? 'hace un instante' : CUANDO.format(new Date(ms));
}

export function AuditLog({
  cargar = leerConfirmaciones,
  cargarEstados = leerEstadosDeConfirmacion,
  cargarLatido = leerLatidoCola,
  recargarToken = 0,
  limiteRpsPago,
  uidActual = null,
  cancelar = cancelarConLaCallable,
}: {
  /** Inyectable para test; en la app es la lectura real de Firestore. */
  cargar?: () => Promise<Confirmacion[]>;
  /**
   * Cambiar este número recarga el registro. Es un PROP y no una `key` a propósito: con `key` el
   * componente se remontaba, y un componente recién montado no puede saber qué filas son nuevas
   * —pierde la lista anterior—, que es justo lo que hace falta para destacar la confirmación que
   * acaba de aparecer.
   */
  recargarToken?: number;
  /**
   * Ritmo del endpoint de pago (BB-1651), para poder dar un mínimo teórico mientras el trabajo en
   * curso no tiene ritmo real todavía. `undefined` es un estado válido: el contador dice "calculando".
   */
  limiteRpsPago?: number;
  /**
   * Estado real de las transferencias por confirmación (BB-1643), indexado por previewId. Se carga
   * junto al registro y en la misma acción de "Actualizar": son dos colecciones que se leen para
   * pintar una sola pantalla.
   */
  cargarEstados?: () => Promise<Map<string, EstadoTransferenciasDeConfirmacion>>;
  /**
   * Latido de la cola (BB-2623/BB-2625): con él, "sin resolver" se separa en "esperando turno" y "la
   * cola no avanza". `null` (o un fallo al leerlo) deja la pantalla como antes de BB-2625.
   */
  cargarLatido?: () => Promise<LatidoCola | null>;
  /**
   * Quién está mirando (BB-1676). Decide en qué filas se ofrece la X, porque solo quien confirmó puede
   * cancelar. `null` —sin sesión resuelta todavía— no muestra ninguna: el lado seguro es no ofrecer un
   * botón que va a dar permission-denied.
   */
  uidActual?: string | null;
  /** Inyectable para test; en la app es la callable. */
  cancelar?: (previewId: string) => Promise<void>;
}) {
  const [confirmaciones, setConfirmaciones] = useState<Confirmacion[] | null>(null);
  const [estados, setEstados] = useState<Map<string, EstadoTransferenciasDeConfirmacion>>(new Map());
  const [latido, setLatido] = useState<LatidoCola | null>(null);
  /**
   * El reloj con el que se reinterpreta cada fila. Se pone al día en cada carga y, mientras haya algo
   * vivo, cada segundo (más abajo).
   */
  const [ahoraMs, setAhoraMs] = useState(() => Date.now());
  /**
   * previewIds que han aparecido DESDE la carga anterior. Es lo que se destaca en pantalla: tras
   * confirmar, la fila nueva tiene que llamar la atención sola, sin que nadie compare a mano.
   *
   * En la PRIMERA carga no hay nada nuevo: todo lo que se ve es historial. Marcarlo todo destacaría
   * la pantalla entera, que es lo mismo que no destacar nada.
   */
  const [nuevas, setNuevas] = useState<Set<string>>(new Set());
  const idsVistos = useRef<Set<string> | null>(null);
  const [cargando, setCargando] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const recargar = useCallback(async () => {
    setCargando(true);
    setError(null);
    try {
      const leidas = await cargar();
      setConfirmaciones(leidas);

      const ids = new Set(leidas.map((c) => c.previewId));
      const previos = idsVistos.current;
      setNuevas(previos === null ? new Set() : new Set([...ids].filter((id) => !previos.has(id))));
      idsVistos.current = ids;
      // Por separado y tolerante a fallo: el registro es la evidencia y tiene que pintarse aunque el
      // cruce diario no se pueda leer. Si esto falla, cada fila cae en su estado "todavía sin cruce",
      // que es un mensaje honesto, en vez de tumbar la pantalla entera.
      try {
        setEstados(await cargarEstados());
      } catch {
        setEstados(new Map());
      }
      // Igual de tolerante: sin latido, las filas se pintan como antes de BB-2625.
      try {
        setLatido(await cargarLatido());
      } catch {
        setLatido(null);
      }
      setAhoraMs(Date.now());
    } catch (err) {
      // El motivo importa: sin el claim `caes_console` esto da permission-denied, y ese mensaje es
      // accionable ("pide acceso"), mientras que "no se pudo cargar" manda a nadie a ningún sitio.
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setCargando(false);
    }
  }, [cargar, cargarEstados, cargarLatido]);

  useEffect(() => {
    void recargar();
    // `recargarToken` en las dependencias: es lo que dispara la recarga cuando App confirma un pago.
  }, [recargar, recargarToken]);

  // Lo que se pinta: cada confirmación reinterpretada con el latido de la cola (`aplicarLatido`).
  const vistas = useMemo(
    () => confirmaciones?.map((c) => aplicarLatido(c, latido, ahoraMs)) ?? null,
    [confirmaciones, latido, ahoraMs],
  );

  // "Esperando turno" también está vivo: se sigue releyendo para verlo pasar a "en curso".
  const hayAlgoEnCurso = (vistas ?? []).some(
    (c) => c.estado === 'en-curso' || c.estado === 'esperando-turno',
  );

  /**
   * BB-1652, pieza 3: excepción DELIBERADA a "esta pantalla no se recarga sola".
   *
   * La regla original sigue en pie —una pantalla de evidencia que se recarga bajo el cursor de quien
   * está leyendo es peor que una que espera— y por eso la excepción es la mínima posible: solo se
   * recarga MIENTRAS haya una confirmación `en-curso`, y en cuanto todas terminan vuelve a ser
   * estática. Nada se mueve cuando no hay nada moviéndose de verdad.
   *
   * POR QUÉ POLLING Y NO `onSnapshot`, que era la otra opción del ticket: el worker escribe un
   * `progress` por chunk, y con chunks de 200 viajes una confirmación grande son decenas de miles de
   * documentos (5,5 M / 200 ≈ 27.600). Un listener los recibiría todos y repintaría la pantalla otras
   * tantas veces, para mover un contador que a nadie le importa al segundo. El polling acota eso a un
   * ritmo conocido, reutiliza la lectura que ya existe (registro + cruce, la misma que el botón
   * Actualizar) y no introduce un patrón nuevo de Firestore en un repo que no tiene ninguno.
   *
   * El intervalo son 10 s porque un chunk de 200 viajes a 40 req/s tarda ~5 s largos: cada poll trae,
   * como mucho, un dato nuevo. Más rápido serían lecturas sin información.
   */
  useEffect(() => {
    if (!hayAlgoEnCurso) return;
    const id = window.setInterval(() => void recargar(), MS_ENTRE_POLLINGS);
    return () => window.clearInterval(id);
  }, [hayAlgoEnCurso, recargar]);

  /**
   * Y entre pollings, el reloj local avanza para que el contador no se quede congelado 10 s. Solo
   * mueve el `ahora` con el que se recalcula: no lee nada, no inventa progreso — si el trabajo se
   * queda callado, `calcularRestante` acaba diciendo "sin señal" en vez de seguir bajando.
   */
  useEffect(() => {
    if (!hayAlgoEnCurso) return;
    const id = window.setInterval(() => setAhoraMs(Date.now()), 1000);
    return () => window.clearInterval(id);
  }, [hayAlgoEnCurso]);

  return (
    <Stack gap="md">
      <Group justify="space-between" align="center">
        <div>
          <Title order={2} fz="h5">
            Registro de confirmaciones
          </Title>
          <Text size="sm" c="dimmed">
            Qué se confirmó, con qué filtro, y por quién.
          </Text>
        </div>
        <Button variant="default" size="sm" onClick={() => void recargar()} loading={cargando}>
          Actualizar
        </Button>
      </Group>

      {error !== null && (
        <Alert color="red" title="No se ha podido leer el registro">
          {error}
        </Alert>
      )}

      {vistas === null && cargando && <Loader size="sm" />}

      {vistas !== null && vistas.length === 0 && (
        <Alert color="gray" title="Todavía no hay ninguna confirmación">
          Nadie ha confirmado ningún pago desde esta consola todavía. Cuando alguien lo haga aparecerá
          aquí, con el filtro que se aplicó, quién lo hizo y qué pasó después.
        </Alert>
      )}

      {vistas?.map((c) => (
        <FilaConfirmacion
          key={c.previewId}
          c={c}
          estadoTransferencias={estados.get(c.previewId)}
          esNueva={nuevas.has(c.previewId)}
          ahoraMs={ahoraMs}
          limiteRpsPago={limiteRpsPago}
          uidActual={uidActual}
          cancelar={cancelar}
          alCancelar={recargar}
        />
      ))}
    </Stack>
  );
}

/**
 * Una confirmación en la lista.
 *
 * Compacta por defecto y con desplegable (2026-08-12): con los detalles siempre abiertos, cada
 * confirmación ocupaba media pantalla y el registro se volvía imposible de recorrer en cuanto había
 * más de tres.
 *
 * QUÉ SE COLAPSA Y QUÉ NO, que es la única decisión de peso aquí.
 *
 * DENTRO del desplegable va TODO el detalle, avisos incluidos: criterios, progreso, enviados tras el
 * dedupe, snapshot, el motivo del error y los avisos de dedupe y reintentos. Los avisos también se
 * colapsan porque **el estado ya está en la cabecera** —`Fallido`, `Parcial`, `Sin resolver`, con
 * color, icono y palabra—, así que el MOTIVO del fallo es detalle. Con los avisos fuera, una fila
 * fallida cerrada ocupaba lo mismo que abierta y la lista dejaba de poder recorrerse.
 *
 * FUERA, como chip en la cabecera, se queda solo lo que la etiqueta de estado NO puede expresar
 * porque convive con un `Completado`: el dedupe (invariante 1, fanout) y haber confirmado el mismo
 * preview más de una vez. Sin esos dos chips, una fila cerrada se vería impecable escondiendo
 * exactamente lo que hay que mirar.
 */
function FilaConfirmacion({
  c,
  estadoTransferencias,
  esNueva,
  ahoraMs,
  limiteRpsPago,
  uidActual,
  cancelar,
  alCancelar,
}: {
  c: Confirmacion;
  estadoTransferencias: EstadoTransferenciasDeConfirmacion | undefined;
  /** Ha aparecido desde la última carga: se destaca hasta la siguiente. */
  esNueva: boolean;
  /** Reloj inyectado: avanza cada segundo mientras algo está en curso, y es fijo si no. */
  ahoraMs: number;
  limiteRpsPago?: number;
  uidActual: string | null;
  cancelar: (previewId: string) => Promise<void>;
  /** Recarga el registro tras cancelar: la fila tiene que repintarse como cancelada. */
  alCancelar: () => Promise<void>;
}) {
  const p = PRESENTACION[c.estado];
  const [abierta, setAbierta] = useState(false);
  const idDetalle = `detalle-${c.previewId}`;
  const [pidiendoCancelar, setPidiendoCancelar] = useState(false);

  const criterios = describirCriterios(c.criterios);
  // Viajes en alarma (sin transferencia, aceptados sin mover, fallos sin clasificar...). Va a la cabecera por la misma razón que el
  // chip de dedupe: convive con un "Completado" —la confirmación fue bien, el pago no— así que la
  // etiqueta de estado no lo delata y dentro del desplegable solo lo vería quien abre la fila.
  const nQuePidenAtencion =
    estadoTransferencias === undefined ? 0 : nViajesQuePidenAtencion(estadoTransferencias);
  // BB-2625: los rechazos de Stripe van en su propio chip, ambar. Siguen sin pagarse y Soporte tiene
  // que verlos, pero meterlos en el rojo era decir que la consola fallo, y no es verdad.
  const nRechazadosPorStripe =
    estadoTransferencias === undefined ? 0 : nViajesRechazadosPorStripe(estadoTransferencias);

  // BB-1652: cuánto queda, solo mientras el trabajo está vivo. En un estado terminal no hay nada que
  // estimar y pintarlo seria ruido.
  // Cuánto lleva encolada sin empezar. Solo se usa para decidir si el aviso sube de tono.
  const horasEnCola =
    c.estado === 'en-cola' && c.iniciadaMs !== null ? (ahoraMs - c.iniciadaMs) / 3_600_000 : 0;

  const restante: Restante | null =
    c.estado === 'en-curso'
      ? calcularRestante({
          total: c.resumen?.nViajes ?? null,
          nEnviados: c.progreso?.nEnviados ?? null,
          iniciadaMs: c.iniciadaMs,
          ultimaSenalMs: c.ultimaSenalMs,
          muestras: c.muestrasProgreso,
          ahoraMs,
          limiteRpsPago,
        })
      : null;

  // Latido solo mientras el trabajo está vivo; destello una vez al aparecer. Las dos cosas son
  // decoración: la información que aportan va TAMBIÉN en texto (el badge "nuevo" y la etiqueta de
  // estado), porque el movimiento no es un canal de información para todo el mundo — y en
  // `prefers-reduced-motion` las animaciones se apagan (ver app.css).
  const clases = ['fila-log'];
  if (esNueva) clases.push('fila-log-nueva');
  if (c.estado === 'en-curso') clases.push('fila-log-viva');

  return (
    <Card withBorder padding="sm" className={clases.join(' ')}>
      <Group justify="space-between" align="center" wrap="nowrap" gap="xs">
      {/* Toda la cabecera es el disparador, no un icono de 14px: el area de clic es la fila entera.
          <button> de verdad (via UnstyledButton) y no un div con onClick, para que funcione con
          teclado y lo anuncie un lector de pantalla.

          La X vive FUERA de este botón, como hermana: un <button> dentro de otro <button> es HTML
          inválido y el navegador decide solo cuál de los dos recibe el clic. */}
      <UnstyledButton
        onClick={() => setAbierta((v) => !v)}
        aria-expanded={abierta}
        aria-controls={idDetalle}
        className="fila-log-cabecera"
        style={{ flex: 1, minWidth: 0 }}
      >
        <Group justify="space-between" align="center" wrap="nowrap" gap="sm">
          <Group gap="xs" wrap="nowrap" style={{ minWidth: 0 }}>
            {abierta ? (
              <IconChevronDown size={16} aria-hidden="true" />
            ) : (
              <IconChevronRight size={16} aria-hidden="true" />
            )}
            {/* El destello se lo pierde quien no mire en ese segundo, y no existe si el sistema pide
                menos movimiento. El badge es la versión permanente y legible de lo mismo. */}
            {esNueva && (
              <Badge color="rosaBB" variant="filled" size="sm">
                nuevo
              </Badge>
            )}
            <Badge
              color={p.color}
              variant="light"
              leftSection={<p.Icono size={14} aria-hidden="true" />}
            >
              {p.etiqueta}
            </Badge>
            {/* Una confirmación en mock NO movió dinero, y no puede parecer que sí. Va en la
                cabecera, no en el detalle: es lo que distingue una demo de un pago real.
                Se intentó acortar a "DEMO" al compactar la fila y lo tumbó su test, con razón: en
                una lista donde el resto de filas SÍ movieron dinero, la etiqueta corta obliga a
                saberse el vocabulario. El texto entero se queda. */}
            {c.backendMode === 'mock' && (
              <Badge className="chip-demo" variant="filled">
                DEMO · no se movió dinero
              </Badge>
            )}
            {/* De qué programa salió (BB-2004). Solo se marca el que no es el de siempre: una
                confirmación de soft killers paga viajes que Data rechazó, y en una lista donde el
                resto son elegibles eso tiene que verse sin abrir la fila. */}
            {c.criterios?.programa === 'softKiller' && (
              <Badge color="rosaBB" variant="outline" size="sm">
                {nombreDePrograma('softKiller')}
              </Badge>
            )}
            {/* Lo único que NO se colapsa, porque la etiqueta de estado no puede decirlo: los dos
                casos que conviven con un "Completado". En palabras, no solo en color. */}
            {c.huboDedupe && (
              <Badge color="orange" variant="light" size="sm">
                dedupe
              </Badge>
            )}
            {c.nIntentos > 1 && (
              <Badge color="orange" variant="light" size="sm">
                {c.nIntentos} intentos
              </Badge>
            )}
            {/* Amarillo, no rojo (decidido por Javi, 2026-10-07): el rojo de la cabecera es para el estado
                de la confirmacion (Fallido, Sin resolver, La cola no avanza); esto es un aviso del cruce. */}
            {nQuePidenAtencion > 0 && (
              // Relleno con texto oscuro: en "light" el texto amarillo no se lee, y oscurecerlo lo dejaba
              // igual que el naranja de "rechazados por Stripe", que es otra cosa.
              <Badge color="yellow" variant="filled" size="sm" autoContrast>
                {formatearNumero(nQuePidenAtencion)} a revisar
              </Badge>
            )}
            {nRechazadosPorStripe > 0 && (
              <Badge color="orange" variant="light" size="sm">
                {formatearNumero(nRechazadosPorStripe)} rechazados por Stripe
              </Badge>
            )}
            {/* El tiempo que queda se lee sin abrir la fila: es lo que decide si esperas o te vas a
                hacer otra cosa. `atascado` va en ambar porque ya no es informacion, es un aviso. */}
            {restante !== null && (
              <Badge
                color={restante.tipo === 'atascado' ? 'orange' : 'rosaBB'}
                variant="light"
                size="sm"
              >
                {describirRestanteCorto(restante)}
              </Badge>
            )}
            <Text size="sm" truncate>
              {formatearCuando(c.iniciadaMs)} · {c.actor.email}
            </Text>
          </Group>

          {c.resumen !== null && (
            <Group gap="xs" wrap="nowrap">
              <Text fw={600} size="sm">
                {formatearEur(c.resumen.importeTotalEur)}
              </Text>
              <Text size="sm" c="dimmed">
                · {formatearNumero(c.resumen.nViajes)} viajes
              </Text>
            </Group>
          )}
        </Group>
      </UnstyledButton>

        {/* BB-1676. Solo en las filas propias y con algo que parar (`puedeCancelar`), porque un botón
            que el backend va a rechazar es peor que ninguno. El `Tooltip` y el `aria-label` dicen qué
            hace: una X a secas en una lista de pagos se lee como "borrar este registro", que es
            exactamente lo que NO hace. */}
        {puedeCancelar(c, uidActual) && (
          <Tooltip label="Cancelar lo que queda por pagar">
            <ActionIcon
              variant="subtle"
              color="red"
              aria-label={`Cancelar la confirmación de ${formatearCuando(c.iniciadaMs)}`}
              onClick={() => setPidiendoCancelar(true)}
            >
              <IconX size={16} />
            </ActionIcon>
          </Tooltip>
        )}
      </Group>

      <ModalCancelar
        c={c}
        abierto={pidiendoCancelar}
        cerrar={() => setPidiendoCancelar(false)}
        cancelar={cancelar}
        alCancelar={alCancelar}
      />

      <Collapse in={abierta}>
        <div id={idDetalle}>
          <Stack gap={2} mt="sm">
            {criterios.map((linea) => (
              <Text key={linea} size="sm" c="dimmed">
                {linea}
              </Text>
            ))}
          </Stack>

          {c.progreso !== null && (
            <Text size="sm" mt="xs">
              Progreso: {formatearNumero(c.progreso.nEnviados)} enviados ·{' '}
              {formatearNumero(c.progreso.nOk)} ok
              {c.progreso.nError > 0 && ` · ${formatearNumero(c.progreso.nError)} con error`}
            </Text>
          )}

          {restante !== null && (
            <Text size="sm" mt="xs" c={restante.tipo === 'atascado' ? 'orange' : undefined}>
              {restante.tipo === 'estimando' &&
                `Faltan ${formatearNumero(restante.viajesPendientes)} viajes: ${formatearDuracion(
                  restante.segundos,
                )} ${
                  // Se dice SOBRE QUÉ se midió: "al ritmo actual" a secas era falso cuando el ritmo
                  // cambiaba. Ya no hay rama de "ritmo medio desde que empezó" porque ese cálculo se
                  // eliminó — con tandas que esperan en la cola medía la espera, no el trabajo.
                  `al ritmo de ${formatearDuracion(restante.ventanaSegundos).replace('~', 'los últimos ')}`
                }. Es un mínimo — los reintentos solo pueden alargarlo.`}
              {restante.tipo === 'sin-senal' &&
                'Acaba de empezar: todavía no hay ritmo real con el que estimar cuánto queda.'}
              {restante.tipo === 'deberia-haber-acabado' &&
                'Ya se han enviado todos los viajes; falta que el worker cierre el último chunk.'}
              {restante.tipo === 'atascado' &&
                `Lleva ${formatearDuracion(restante.segundosSinSenal)} sin dar señales. Puede estar ` +
                  'atascado: no se estima nada, porque un contador bajando afirmaría un progreso que ' +
                  'no se está viendo.'}
            </Text>
          )}

          {c.nViajesEnviados !== null && (
            <Text size="sm" mt="xs">
              Enviados a Backend tras el dedupe: {formatearNumero(c.nViajesEnviados)}
            </Text>
          )}

          {c.snapshotTable !== null && (
            <Text size="xs" c="dimmed" mt="xs">
              Snapshot: {c.snapshotTable}
            </Text>
          )}

          <EstadoTransferencias estado={estadoTransferencias} esMock={c.backendMode === 'mock'} />

          {/* El reintento sigue sin poder esconderse: su chip está en la cabecera. Aquí dentro va el
              detalle de por qué importa. */}
          {c.nIntentos > 1 && (
            <Alert color="orange" mt="sm" title={`Se confirmó ${c.nIntentos} veces`}>
              Este mismo preview se envió {c.nIntentos} veces. El endpoint de pago es idempotente, así
              que no debería haberse pagado por duplicado, pero conviene revisar por qué hubo
              reintentos.
            </Alert>
          )}

          {/* Invariante 1: si lo enviado no cuadra con lo previsualizado, hubo fanout. */}
          {c.huboDedupe && (
            <Alert color="orange" mt="sm" title="El dedupe quitó filas">
              Se previsualizaron {formatearNumero(c.resumen?.nViajes ?? 0)} viajes y se enviaron{' '}
              {formatearNumero(c.nViajesEnviados ?? 0)}. Una diferencia aquí significa que la selección
              traía el mismo viaje más de una vez.
            </Alert>
          )}

          {c.estado === 'en-cola' && (
            <Alert
              // Naranja solo cuando lleva demasiado sin empezar. El color no puede gritar por algo
              // que es normal: con la cuota diaria agotada, esperar horas es el comportamiento
              // correcto desde BB-1667.
              color={horasEnCola >= HORAS_EN_COLA_PARA_AVISAR ? 'orange' : 'gray'}
              mt="sm"
              title={
                c.progreso !== null
                  ? 'Parte enviada, el resto esperando su tanda'
                  : 'Encolada, sin empezar todavía'
              }
            >
              {/* BB-2625: "en cola" ya no implica que no se haya enviado nada. Con una tanda futura en
                  el plan, la primera puede haberse pagado; decir "no se ha movido dinero" entonces
                  seria mentir justo en la direccion tranquilizadora. */}
              {c.progreso !== null ? (
                <>
                  Ya se enviaron{' '}
                  <strong>{formatearNumero(c.progreso.nEnviados)} viajes</strong> a Backend (
                  {formatearNumero(c.progreso.nOk)} aceptados), así que esta confirmación ya ha movido
                  dinero. Lo que queda espera a la siguiente tanda que permite la cuota diaria del
                  endpoint.
                </>
              ) : (
                <>
                  Los viajes están encolados y ningún envío ha terminado aún, así que{' '}
                  <strong>no se ha movido dinero</strong>. Es normal: si la cuota diaria del endpoint
                  está agotada, las tandas esperan y se reintentan solas hasta que se renueva.
                </>
              )}
              {c.proximaTandaMs !== null && (
                <Text size="sm" mt="xs">
                  Siguiente tanda programada: {formatearCuando(c.proximaTandaMs)}.
                </Text>
              )}
              {horasEnCola >= HORAS_EN_COLA_PARA_AVISAR && (
                <Text size="sm" mt="xs">
                  Lleva {formatearDuracion(Math.round((ahoraMs - (c.iniciadaMs ?? ahoraMs)) / 1000))} sin
                  empezar. Si no es por la cuota, conviene mirar si la cola está pausada.
                </Text>
              )}
            </Alert>
          )}

          {/* BB-1676. Lo que esta pantalla NO puede dejar ambiguo: cancelar no devuelve el dinero ya
              enviado. La cifra va delante, no un "puede que se haya pagado algo".

              El título no repite la palabra del badge: en una fila cancelada habría dos "Cancelada" a
              diez píxeles y esto tiene que decir algo que la etiqueta no dice. */}
          {c.cancelada !== null && (
            <Alert color="gray" mt="sm" title="Se paró lo que quedaba por pagar">
              {c.cancelada.email} la canceló el {formatearCuando(c.cancelada.ms)}, con{' '}
              {formatearNumero(c.cancelada.tandasPendientes)}{' '}
              {c.cancelada.tandasPendientes === 1 ? 'tanda' : 'tandas'} sin procesar.
              {c.cancelada.nViajesYaEnviados > 0 ? (
                <Text size="sm" mt="xs">
                  <strong>
                    {formatearNumero(c.cancelada.nViajesYaEnviados)} viajes ya se habían enviado
                  </strong>{' '}
                  y eso no se deshace: Backend los aceptó y su certificación sigue su curso. Cancelar
                  solo evita los que quedaban.
                </Text>
              ) : (
                <Text size="sm" mt="xs">
                  No se había enviado ningún viaje todavía, así que{' '}
                  <strong>no se movió dinero</strong>.
                </Text>
              )}
            </Alert>
          )}

          {c.estado === 'esperando-turno' && (
            <Alert color="gray" mt="sm" title="Esperando turno en la cola">
              La cola paga una tanda cada vez y ahora está sirviendo a otra confirmación: sigue
              avanzando, así que esta continuará cuando le toque. No hay que hacer nada.
              {c.progreso !== null && (
                <Text size="sm" mt="xs">
                  Lleva {formatearNumero(c.progreso.nEnviados)} viajes enviados.
                </Text>
              )}
            </Alert>
          )}

          {c.estado === 'cola-parada' && (
            <Alert color="red" mt="sm" title="La cola no avanza">
              Ninguna tanda de ninguna confirmación ha terminado en los últimos 10 minutos. La cola
              puede estar pausada, o todas sus tandas fallando y reintentándose. Avisa a Data: desde
              aquí no se puede saber cuál de las dos.
              {c.progreso !== null && (
                <Text size="sm" mt="xs">
                  Último progreso de esta confirmación: {formatearNumero(c.progreso.nEnviados)} viajes
                  enviados.
                </Text>
              )}
            </Alert>
          )}

          {c.estado === 'sin-resolver' && (
            <Alert color="red" mt="sm" title="Sin resultado registrado">
              Se registró la intención de confirmar pero nunca su resultado
              {c.progreso !== null
                ? `, y el último progreso conocido fue ${formatearNumero(c.progreso.nEnviados)} viajes enviados.`
                : ', y no hay ningún progreso registrado, así que no se sabe cuántos viajes llegaron a enviarse.'}{' '}
              Hay que reconciliarlo a mano.
              {c.error !== null && (
                <Text size="sm" mt="xs">
                  {c.error}
                </Text>
              )}
            </Alert>
          )}

          {c.error !== null && c.estado !== 'sin-resolver' && (
            <Alert color="red" mt="sm" title="Backend devolvió un error">
              {c.error}
            </Alert>
          )}

          {/* Honestidad sobre de dónde sale el estado: cuando faltan latidos `progress`, "en curso" y
              "sin resolver" salen de comparar antigüedad, no de un hecho. */}
          {c.inferido && (c.estado === 'en-curso' || c.estado === 'sin-resolver') && (
            <Text size="xs" c="dimmed" mt="xs">
              Estado deducido por antigüedad, no registrado: sin eventos de progreso no se puede
              distinguir un proceso vivo de uno que murió.
            </Text>
          )}
        </div>
      </Collapse>
    </Card>
  );
}

/**
 * Llamada real a la callable. Envuelta aquí para que `<AuditLog>` pueda recibir un doble en los tests
 * sin montar Firebase, igual que `cargar`/`cargarEstados`.
 */
async function cancelarConLaCallable(previewId: string): Promise<void> {
  await llamarCancelar({ previewId });
}

/**
 * Confirmación antes de cancelar (BB-1676).
 *
 * Existe porque parar un pago a medias no es reversible en ninguna dirección: lo enviado sigue su
 * curso y lo parado hay que volver a confirmarlo. Así que el modal dice las TRES cosas que alguien
 * necesita saber antes de pulsar, y ninguna es genérica:
 *
 * 1. **Lo ya enviado no se deshace**, con la cifra delante.
 * 2. **Una tanda que ya está pagando termina** — la cancelación se comprueba al empezar cada tanda.
 * 3. **El registro se queda.** Es lo que Javi pidió explícitamente: «no quiero eliminar el log, eso
 *    quiero que quede registrado». Decirlo aquí evita que alguien pulse la X creyendo que limpia la
 *    lista.
 */
function ModalCancelar({
  c,
  abierto,
  cerrar,
  cancelar,
  alCancelar,
}: {
  c: Confirmacion;
  abierto: boolean;
  cerrar: () => void;
  cancelar: (previewId: string) => Promise<void>;
  alCancelar: () => Promise<void>;
}) {
  const [enviando, setEnviando] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Lo que se sabe ANTES de cancelar sale del último `progress`; el número exacto de tandas paradas lo
  // devuelve el backend y se ve luego en la fila. Aquí no se estima: decir "se pararán unos N" cuando
  // el dato no está sería inventarse la cifra que más importa.
  const yaEnviados = c.progreso?.nEnviados ?? 0;
  const total = c.resumen?.nViajes ?? null;

  const alPulsar = async () => {
    setEnviando(true);
    setError(null);
    try {
      await cancelar(c.previewId);
      cerrar();
      await alCancelar();
    } catch (err) {
      // El mensaje del backend es accionable: dice si la confirmación es de otra persona, si ya
      // terminó, o si ya estaba cancelada. Se muestra tal cual en vez de un "no se pudo cancelar".
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setEnviando(false);
    }
  };

  return (
    <Modal opened={abierto} onClose={cerrar} title="¿Cancelar lo que queda por pagar?" centered>
      <Stack gap="sm">
        <Text size="sm">
          Las tandas que aún no se han procesado dejarán de enviarse. Esta confirmación es de{' '}
          {formatearCuando(c.iniciadaMs)}
          {total !== null && ` y seleccionó ${formatearNumero(total)} viajes`}.
        </Text>

        {yaEnviados > 0 ? (
          <Alert color="orange" title="Lo ya enviado no se deshace">
            <strong>{formatearNumero(yaEnviados)} viajes ya se han enviado</strong> al endpoint de pago.
            Backend los aceptó y su certificación sigue su curso: cancelar no los recupera.
          </Alert>
        ) : (
          <Alert color="gray" title="Todavía no se ha movido dinero">
            Ninguna tanda ha terminado, así que no hay ningún viaje enviado que quede a medias.
          </Alert>
        )}

        <Text size="sm" c="dimmed">
          Si una tanda ya está pagando, termina la que tiene entre manos: la cancelación se comprueba al
          empezar cada tanda, no viaje a viaje.
        </Text>

        <Text size="sm" c="dimmed">
          El registro <strong>no se borra</strong>: esta confirmación se queda en la lista y se añade
          quién la canceló y cuándo.
        </Text>

        {error !== null && (
          <Alert color="red" title="No se ha podido cancelar">
            {error}
          </Alert>
        )}

        <Group justify="flex-end" gap="sm">
          <Button variant="default" onClick={cerrar} disabled={enviando}>
            Dejarla como está
          </Button>
          <Button color="red" onClick={() => void alPulsar()} loading={enviando}>
            Cancelar el envío
          </Button>
        </Group>
      </Stack>
    </Modal>
  );
}
